import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AISummary } from "../components/AISummary";
import { BillsCalendar } from "../components/BillsCalendar";
import { trackBillPaid, trackCalendarSync } from "../utils/analytics";
import { 
  Receipt, 
  Calendar,
  CalendarDays,
  List,
  RefreshCw,
  Pencil,
  EyeOff,
  Loader2,
  ArrowRight,
  Clock,
  AlertCircle,
  Plus,
  Trash2,
  CheckCircle2,
  Circle,
  DollarSign,
  CreditCard
} from "lucide-react";
import { toast } from "sonner";

const CADENCE_LABELS = {
  weekly: "Weekly",
  biweekly: "Bi-weekly",
  monthly: "Monthly",
  yearly: "Yearly",
};

const CADENCE_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "biweekly", label: "Bi-weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

export const BillsPage = () => {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [bills, setBills] = useState([]);
  const [creditCards, setCreditCards] = useState([]);
  const [selectedBill, setSelectedBill] = useState(null);
  const [editName, setEditName] = useState("");
  const [calendarEnabled, setCalendarEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [viewMode, setViewMode] = useState("list"); // "list" or "calendar"
  
  // Credit card due date editing
  const [editingCard, setEditingCard] = useState(null);
  const [editDueDay, setEditDueDay] = useState("");
  
  // Add bill modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newBill, setNewBill] = useState({
    merchant: "",
    amount: "",
    cadence: "monthly",
    next_due_date: new Date().toISOString().split("T")[0]
  });
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [markingPaid, setMarkingPaid] = useState(null);
  const [paymentSummary, setPaymentSummary] = useState(null);

  const fetchBills = useCallback(async () => {
    setLoading(true);
    try {
      const [billsRes, paymentRes, cardsRes] = await Promise.all([
        api.get("/bills/upcoming"),
        api.get("/bills/payment-status"),
        api.get("/credit-cards/bills")
      ]);
      setBills(billsRes.data);
      setPaymentSummary(paymentRes.data?.summary || null);
      setCreditCards(cardsRes.data || []);
    } catch (error) {
      console.error("Failed to fetch bills:", error);
      toast.error("Failed to load bills");
    } finally {
      setLoading(false);
    }
  }, [api]);

  const checkCalendarStatus = useCallback(async () => {
    try {
      const response = await api.get("/calendar/connections");
      setCalendarConnected(response.data.some(c => c.status === "connected"));
    } catch (error) {
      console.error("Failed to check calendar status:", error);
    }
  }, [api]);

  useEffect(() => {
    fetchBills();
    checkCalendarStatus();
  }, [fetchBills, checkCalendarStatus]);

  const handleUpdateCardDueDate = async (cardId) => {
    if (!editDueDay || editDueDay < 1 || editDueDay > 28) {
      toast.error("Due day must be between 1 and 28");
      return;
    }
    
    try {
      await api.patch(`/credit-cards/${cardId}/due-date`, {
        due_day: parseInt(editDueDay)
      });
      toast.success("Due date updated");
      setEditingCard(null);
      fetchBills();
    } catch (error) {
      toast.error("Failed to update due date");
    }
  };

  const handleRefreshBills = async () => {
    setRefreshing(true);
    try {
      await api.post("/bills/refresh");
      await fetchBills();
      toast.success("Bills refreshed");
    } catch (error) {
      toast.error("Failed to refresh bills");
    } finally {
      setRefreshing(false);
    }
  };

  const handleToggleCalendar = async (billId, enabled) => {
    try {
      await api.patch(`/bills/${billId}`, { calendar_enabled: enabled });
      setBills(prev => prev.map(b => 
        b.id === billId ? { ...b, calendar_enabled: enabled } : b
      ));
      toast.success(enabled ? "Added to calendar" : "Removed from calendar");
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleOpenEdit = (bill) => {
    setSelectedBill(bill);
    setEditName(bill.merchant_display || bill.merchant);
    setCalendarEnabled(bill.calendar_enabled);
  };

  const handleSaveBill = async () => {
    if (!selectedBill) return;
    
    setSaving(true);
    try {
      await api.patch(`/bills/${selectedBill.id}`, {
        merchant_display: editName !== selectedBill.merchant ? editName : null,
        calendar_enabled: calendarEnabled
      });
      
      setBills(prev => prev.map(b => 
        b.id === selectedBill.id 
          ? { 
              ...b, 
              merchant_display: editName !== selectedBill.merchant ? editName : null,
              calendar_enabled: calendarEnabled 
            } 
          : b
      ));
      
      toast.success("Bill updated");
      setSelectedBill(null);
    } catch (error) {
      toast.error("Failed to update bill");
    } finally {
      setSaving(false);
    }
  };

  const handleAddBill = async () => {
    if (!newBill.merchant || !newBill.amount) {
      toast.error("Please fill in merchant and amount");
      return;
    }

    setAdding(true);
    try {
      await api.post("/bills/manual", {
        merchant: newBill.merchant,
        amount: parseFloat(newBill.amount),
        cadence: newBill.cadence,
        next_due_date: newBill.next_due_date
      });

      toast.success("Bill added");
      setShowAddModal(false);
      setNewBill({
        merchant: "",
        amount: "",
        cadence: "monthly",
        next_due_date: new Date().toISOString().split("T")[0]
      });
      fetchBills();
    } catch (error) {
      toast.error("Failed to add bill");
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteBill = async (billId) => {
    setDeleting(billId);
    try {
      await api.delete(`/bills/${billId}`);
      toast.success("Bill deleted");
      setBills(bills.filter(b => b.id !== billId));
    } catch (error) {
      toast.error("Failed to delete bill");
    } finally {
      setDeleting(null);
    }
  };

  const handleMarkAsPaid = async (billId, e) => {
    if (e) e.stopPropagation();
    setMarkingPaid(billId);
    try {
      await api.post(`/bills/${billId}/pay`);
      toast.success("Bill marked as paid! Transaction recorded.");
      
      // Track bill payment for analytics
      const bill = bills.find(b => b.id === billId);
      if (bill) {
        trackBillPaid(billId, bill.avg_amount);
      }
      
      setBills(bills.map(b => 
        b.id === billId ? { ...b, is_paid_this_month: true } : b
      ));
      // Refresh payment summary
      const paymentRes = await api.get("/bills/payment-status");
      setPaymentSummary(paymentRes.data?.summary || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark as paid");
    } finally {
      setMarkingPaid(null);
    }
  };

  const handleUnmarkAsPaid = async (billId, e) => {
    if (e) e.stopPropagation();
    setMarkingPaid(billId);
    try {
      await api.delete(`/bills/${billId}/pay`);
      toast.success("Payment status removed");
      setBills(bills.map(b => 
        b.id === billId ? { ...b, is_paid_this_month: false } : b
      ));
      // Refresh payment summary
      const paymentRes = await api.get("/bills/payment-status");
      setPaymentSummary(paymentRes.data?.summary || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove payment");
    } finally {
      setMarkingPaid(null);
    }
  };

  const handleIgnoreBill = async () => {
    if (!selectedBill) return;
    
    setSaving(true);
    try {
      await api.patch(`/bills/${selectedBill.id}`, { enabled: false });
      setBills(prev => prev.filter(b => b.id !== selectedBill.id));
      toast.success("Bill hidden");
      setSelectedBill(null);
    } catch (error) {
      toast.error("Failed to hide bill");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    const currency = user?.currency || "CAD";
    return new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return "Today";
    if (date.toDateString() === tomorrow.toDateString()) return "Tomorrow";
    
    const daysUntil = Math.ceil((date - today) / (1000 * 60 * 60 * 24));
    if (daysUntil <= 7) return `In ${daysUntil} days`;
    
    return date.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
  };

  const getDaysUntil = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.ceil((date - today) / (1000 * 60 * 60 * 24));
  };

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  };

  // Calculate total upcoming
  const totalUpcoming = bills.reduce((sum, b) => sum + b.avg_amount, 0);
  const billsWithCalendar = bills.filter(b => b.calendar_enabled).length;
  const paidBills = bills.filter(b => b.is_paid_this_month);
  const unpaidBills = bills.filter(b => !b.is_paid_this_month);
  const totalPaid = paidBills.reduce((sum, b) => sum + b.avg_amount, 0);
  const totalUnpaid = unpaidBills.reduce((sum, b) => sum + b.avg_amount, 0);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="bills-loading">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-8 w-24 mb-2" />
            <Skeleton className="h-5 w-48" />
          </div>
          <Skeleton className="h-10 w-32 rounded-full" />
        </div>
        <Skeleton className="h-24 rounded-xl" />
        <div className="space-y-3">
          {[1,2,3,4].map((i) => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  // Total credit card debt
  var totalCardDebt = creditCards.reduce(function(s,c){return s + (c.balance || 0);}, 0);
  // Next payment
  var nextBill = unpaidBills.length > 0 ? unpaidBills[0] : null;

  return (
    <div data-testid="bills-page">
      {/* Header */}
      <div className="phead">
        <div><h1>Bills</h1><div className="sub">Everything you owe, in one calm view</div></div>
        <div className="actions" style={{display:"flex",alignItems:"center",gap:10}}>
          <div className="seg" style={{display:"flex",border:"1.5px solid #ECE8DC",borderRadius:50,overflow:"hidden"}}>
            <button style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",fontSize:14,fontWeight:600,border:"none",cursor:"pointer",fontFamily:"inherit",background: viewMode==="list" ? "#14432B" : "transparent",color: viewMode==="list" ? "#F4EFE4" : "#7d7d70"}} onClick={function(){setViewMode("list");}}><List size={16} /> List</button>
            <button style={{display:"flex",alignItems:"center",gap:6,padding:"8px 14px",fontSize:14,fontWeight:600,border:"none",cursor:"pointer",fontFamily:"inherit",background: viewMode==="calendar" ? "#14432B" : "transparent",color: viewMode==="calendar" ? "#F4EFE4" : "#7d7d70"}} onClick={function(){setViewMode("calendar");}}><CalendarDays size={16} /> Calendar</button>
          </div>
          <button className="btn btn-p" onClick={function(){setShowAddModal(true);}}><Plus size={16} stroke="#F4EFE4" /> Add bill</button>
        </div>
      </div>

      {viewMode === "calendar" ? <BillsCalendar bills={bills} formatCurrency={formatCurrency} /> : <>

      {/* Stats Row */}
      <div className="card stats" style={{marginBottom:24}}>
        <div className="st"><div className="lab">Total due</div><div className="v">{formatCurrency(totalCardDebt + totalUnpaid)}</div><div className="sm">{creditCards.length > 0 ? "Across "+creditCards.length+" card"+(creditCards.length!==1?"s":"") : bills.length+" bills"}</div></div>
        <div className="st"><div className="lab">Next payment</div><div className="v" style={{color:"#C68A3C"}}>{nextBill ? formatDate(nextBill.next_due_date) : "None"}</div><div className="sm">{nextBill ? (nextBill.merchant_display || nextBill.merchant)+" · "+formatCurrency(nextBill.avg_amount) : "All caught up"}</div></div>
        <div className="st"><div className="lab">Recurring / month</div><div className="v">{formatCurrency(totalUpcoming)}</div><div className="sm">{bills.length} detected</div></div>
      </div>

      {/* AI Insight */}
      <AISummary page="bills" />

      {/* Credit Cards Section */}
      {creditCards.length > 0 && <>
        <div className="sec-h" style={{margin:"4px 2px 14px"}}>
          <div style={{display:"flex",alignItems:"center",gap:11}}><CreditCard size={19} stroke="#14432B" /><h3 style={{fontSize:20,fontFamily:"'Lora',Georgia,serif",fontWeight:600}}>Credit cards</h3></div>
          <span className="muted" style={{fontSize:14}}>{formatCurrency(totalCardDebt)} due</span>
        </div>
        {creditCards.map(function(card) {
          var usedPct = card.credit_limit > 0 ? ((card.balance / card.credit_limit) * 100).toFixed(1) : 0;
          var available = card.credit_limit - card.balance;
          var barColor = usedPct > 70 ? "#B0603F" : "#3F7D52";
          return (
            <div key={card.id} className="card" style={{padding:"22px 24px",marginBottom:16}}>
              <div style={{display:"flex",alignItems:"flex-start",gap:16}}>
                <div className="ai2" style={{width:44,height:44,borderRadius:12,background:"rgba(198,138,60,.08)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}><CreditCard size={22} stroke="#C68A3C" /></div>
                <div style={{flex:1}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
                    <div>
                      <b style={{fontSize:16,fontWeight:700}}>{card.name}</b>
                      <div className="muted" style={{fontSize:"13.5px",marginTop:3}}>{card.institution_name || "Connected Bank"} ····{card.mask} · <span style={{color:barColor,fontWeight:600}}>{usedPct}% used</span></div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div className="serif" style={{fontSize:22,fontWeight:600,fontFamily:"'Lora',Georgia,serif"}}>{formatCurrency(card.balance)}</div>
                      {card.due_day && <div style={{display:"inline-flex",alignItems:"center",gap:6,fontSize:13,color:"#C68A3C",fontWeight:600,marginTop:2}}><Clock size={13} stroke="#C68A3C" /> Due {card.due_day}{getOrdinalSuffix(card.due_day)}</div>}
                    </div>
                  </div>
                  <div className="bar" style={{height:8,borderRadius:8,background:"#F4EFE4",marginTop:15,overflow:"hidden"}}><i style={{width:usedPct+"%",display:"block",height:"100%",borderRadius:8,background:barColor}} /></div>
                  <div className="muted" style={{fontSize:13,marginTop:9}}>{formatCurrency(available)} available of {formatCurrency(card.credit_limit)}</div>
                </div>
              </div>
            </div>
          );
        })}
      </>}

      {/* Recurring & Subscriptions */}
      <div className="sec-h" style={{margin:"30px 2px 14px"}}>
        <div style={{display:"flex",alignItems:"center",gap:11}}><RefreshCw size={19} stroke="#14432B" /><h3 style={{fontSize:20,fontFamily:"'Lora',Georgia,serif",fontWeight:600}}>Recurring & subscriptions</h3></div>
        <span className="muted" style={{fontSize:14}}>Detected from your spending</span>
      </div>

      {bills.length === 0 ? (
        <div className="card" style={{padding:72,textAlign:"center"}}>
          <div className="empty" style={{display:"flex",flexDirection:"column",alignItems:"center",gap:8}}>
            <div style={{width:56,height:56,borderRadius:16,background:"#DCEAD8",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:8}}><Receipt size={26} stroke="#14432B" /></div>
            <div className="serif" style={{fontSize:23,fontWeight:600,color:"#23241E",marginBottom:6}}>No recurring bills detected</div>
            <div style={{maxWidth:360,color:"#7d7d70"}}>As transactions come in, Tally will automatically detect recurring patterns.</div>
          </div>
        </div>
      ) : (
        <div className="card">
          {bills.map(function(bill) {
            var displayName = bill.merchant_display || bill.merchant;
            var isPaid = bill.is_paid_this_month;
            return (
              <div key={bill.id} className="acct" style={{display:"flex",alignItems:"center",gap:14,padding:"14px 22px",borderBottom:"1px solid #ECE8DC"}} onClick={function(){handleOpenEdit(bill);}}>
                <div className="ai2" style={{width:40,height:40,borderRadius:12,background: isPaid ? "rgba(63,125,82,.12)" : "rgba(63,125,82,.06)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                  {isPaid ? <CheckCircle2 size={20} stroke="#3F7D52" /> : <Receipt size={20} stroke="#3F7D52" />}
                </div>
                <div className="nm" style={{flex:1,minWidth:0,cursor:"pointer"}}>
                  <b style={{display:"block",fontSize:"14.5px",fontWeight:600,textDecoration: isPaid ? "line-through" : "none",opacity: isPaid ? .5 : 1}}>{displayName}</b>
                  <small style={{fontSize:13,color:"#7d7d70"}}>{CADENCE_LABELS[bill.cadence] || bill.cadence} · ~ {bill.next_due_date ? new Date(bill.next_due_date).getDate() + getOrdinalSuffix(new Date(bill.next_due_date).getDate()) : ""}</small>
                </div>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <b className="bal" style={{fontFamily:"'Lora',Georgia,serif",fontSize:16,fontWeight:600}}>{formatCurrency(bill.avg_amount)}</b>
                  {!isPaid ? (
                    <button className="btn btn-g" style={{padding:"8px 15px",display:"inline-flex",alignItems:"center",gap:6,height:36,borderRadius:50,border:"1.5px solid #ECE8DC",background:"#fff",fontSize:13,fontWeight:600,color:"#14432B",cursor:"pointer",fontFamily:"inherit"}} onClick={function(e){e.stopPropagation();handleMarkAsPaid(bill.id,e);}} disabled={markingPaid===bill.id}>
                      {markingPaid===bill.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Paid
                    </button>
                  ) : (
                    <button style={{padding:"8px 12px",borderRadius:50,border:"1.5px solid #ECE8DC",background:"transparent",fontSize:12,color:"#7d7d70",cursor:"pointer",fontFamily:"inherit"}} onClick={function(e){e.stopPropagation();handleUnmarkAsPaid(bill.id,e);}}>Undo</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      </>}

      {/* Edit Bill Dialog */}
      <Dialog open={!!selectedBill} onOpenChange={function(o){if(!o)setSelectedBill(null);}}>
        <DialogContent style={{background:"#fff",borderColor:"#ECE8DC",borderRadius:20,fontFamily:"'Hanken Grotesk',sans-serif"}}>
          <DialogHeader><DialogTitle style={{fontFamily:"'Lora',Georgia,serif"}}>Edit Bill</DialogTitle><DialogDescription>Update "{selectedBill?.merchant_display || selectedBill?.merchant}"</DialogDescription></DialogHeader>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div><Label>Display Name</Label><input style={{width:"100%",height:44,padding:"0 14px",border:"1.5px solid #ECE8DC",borderRadius:12,fontSize:15,fontFamily:"inherit",outline:"none"}} value={editName} onChange={function(e){setEditName(e.target.value);}} /></div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}><Label>Calendar Reminder</Label><Switch checked={calendarEnabled} onCheckedChange={setCalendarEnabled} /></div>
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"space-between",marginTop:12}}>
            <button style={{background:"none",border:"none",color:"#B0603F",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:4}} onClick={handleIgnoreBill}><EyeOff size={14} /> Hide bill</button>
            <div style={{display:"flex",gap:8}}>
              <button className="btn btn-g" onClick={function(){setSelectedBill(null);}}>Cancel</button>
              <button className="btn btn-p" onClick={handleSaveBill} disabled={saving}>{saving ? <Loader2 size={16} className="animate-spin" /> : null} Save</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Bill Dialog */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent style={{background:"#fff",borderColor:"#ECE8DC",borderRadius:20,fontFamily:"'Hanken Grotesk',sans-serif"}}>
          <DialogHeader><DialogTitle style={{fontFamily:"'Lora',Georgia,serif"}}>Add Bill</DialogTitle></DialogHeader>
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div><Label>Merchant</Label><input style={{width:"100%",height:44,padding:"0 14px",border:"1.5px solid #ECE8DC",borderRadius:12,fontSize:15,fontFamily:"inherit",outline:"none"}} placeholder="e.g. Netflix" value={newBill.merchant} onChange={function(e){setNewBill(function(p){return{...p,merchant:e.target.value};});}} /></div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <div><Label>Amount ($)</Label><input type="number" style={{width:"100%",height:44,padding:"0 14px",border:"1.5px solid #ECE8DC",borderRadius:12,fontSize:15,fontFamily:"inherit",outline:"none"}} placeholder="0.00" value={newBill.amount} onChange={function(e){setNewBill(function(p){return{...p,amount:e.target.value};});}} /></div>
              <div><Label>Frequency</Label><Select value={newBill.cadence} onValueChange={function(v){setNewBill(function(p){return{...p,cadence:v};});}}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{CADENCE_OPTIONS.map(function(o){return <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>;})}</SelectContent></Select></div>
            </div>
            <div><Label>Next Due Date</Label><input type="date" style={{width:"100%",height:44,padding:"0 14px",border:"1.5px solid #ECE8DC",borderRadius:12,fontSize:15,fontFamily:"inherit",outline:"none"}} value={newBill.next_due_date} onChange={function(e){setNewBill(function(p){return{...p,next_due_date:e.target.value};});}} /></div>
          </div>
          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:8}}>
            <button className="btn btn-g" onClick={function(){setShowAddModal(false);}}>Cancel</button>
            <button className="btn btn-p" onClick={handleAddBill} disabled={adding}>{adding ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Add</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
